import { useEffect, useState, useCallback, useMemo } from "react";
import Form from "react-bootstrap/Form";
import {
  updateTopHeader,
  updateLoading,
} from "../../../redux/features/common/commonSlice";
import ApiCaller from "../../constants/ApiCaller";
import { useDispatch, useSelector } from "react-redux";
import urls from "../../constants/apiurl";
import { showDangerToast } from "../../constants/toaster";
import NavBar from "../../structure/NavBar";
import { NavBarTop } from "../../structure/NavBarTop";
import InsufficientPermission from "../../custom/InSufficientPermission";

/**
 * OPTIMIZED GlobalSettingList Component
 *
 * Key Optimizations Applied:
 * 1. ✅ Eliminated repetitive form code with reusable components
 * 2. ✅ Optimized state management with useCallback and useMemo
 * 3. ✅ Centralized validation logic
 * 4. ✅ Improved error handling and user feedback
 * 5. ✅ Added loading states for better UX
 * 6. ✅ Reduced code duplication significantly
 */

// Optimized Input Field Component - Reduces repetitive form code
const OptimizedInput = ({
  label,
  value,
  onChange,
  placeholder,
  disabled,
  maxLength = 8,
  type = "text",
  hasError = false,
  errorMessage = "",
}) => (
  <Form.Group className="mb-3">
    <Form.Label className="text-small">{label}</Form.Label>
    <Form.Control
      type={type}
      maxLength={maxLength}
      value={value || ""}
      disabled={disabled}
      placeholder={placeholder}
      className={`form-control ${hasError ? "border-danger" : ""}`}
      onChange={onChange}
    />
    {hasError && <div className="text-danger small mt-1">{errorMessage}</div>}
  </Form.Group>
);

// Loading Button Component
const LoadingButton = ({ onClick, disabled, loading, className, children }) => (
  <button
    onClick={onClick}
    className={className}
    disabled={disabled || loading}
  >
    {loading ? (
      <>
        <span
          className="spinner-border spinner-border-sm me-2"
          role="status"
          aria-hidden="true"
        ></span>
        Loading...
      </>
    ) : (
      children
    )}
  </button>
);

// Optimized Toggle Switch Component for Boolean Fields
const OptimizedToggle = ({
  label,
  checked,
  onChange,
  disabled,
  id,
}) => (
  <Form.Group className="mb-3">
    <div className="d-flex align-items-center justify-content-between">
      <Form.Label className="text-small mb-0 me-2" style={{ minWidth: '120px' }}>
        {label}
      </Form.Label>
      <Form.Check
        type="switch"
        id={id}
        checked={checked || false}
        disabled={disabled}
        onChange={onChange}
        className="mb-0"
      />
    </div>
  </Form.Group>
);

// Optimized Decimal Input Component for Double Fields
const OptimizedDecimalInput = ({
  label,
  value,
  onChange,
  placeholder,
  disabled,
  maxLength = 8,
  hasError = false,
  errorMessage = "",
}) => (
  <Form.Group className="mb-3">
    <Form.Label className="text-small">{label}</Form.Label>
    <Form.Control
      type="text"
      maxLength={maxLength}
      value={value || ""}
      disabled={disabled}
      placeholder={placeholder}
      className={`form-control ${hasError ? "border-danger" : ""}`}
      onChange={onChange}
    />
    {hasError && <div className="text-danger small mt-1">{errorMessage}</div>}
  </Form.Group>
);
export const GlobalSettingList = (props) => {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state?.userData);

  // Optimized state management
  const [datalist, setDatalist] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [object, setObject] = useState([]);
  const [allTypeOfAds, setAllTypeOfAds] = useState([]);

  // Optimized input handlers with validation
  const handleNumberInput = useCallback(
    (field, maxLen = 8) =>
      (e) => {
        const value = e.target.value.replace(/[^0-9]/g, "");
        if (value.length <= maxLen) {
          setDatalist((prev) => ({ ...prev, [field]: value }));
        }
      },
    []
  );

  // Optimized matrix rate handler
  const handleRateInputChange = useCallback((index, field, value) => {
    const regex = /^\d*\.?\d*$/;
    if (regex.test(value)) {
      setObject((prev) => {
        const newArray = [...prev];
        newArray[index][field] = value;
        return newArray;
      });
    }
  }, []);

  // Optimized decimal input handler for referral fields
  const handleDecimalInput = useCallback(
    (field) =>
      (e) => {
        const value = e.target.value;
        const regex = /^\d*\.?\d*$/;
        if (regex.test(value) || value === "") {
          setDatalist((prev) => ({ ...prev, [field]: value }));
        }
      },
    []
  );

  // Optimized boolean toggle handler for referral fields
  const handleBooleanToggle = useCallback(
    (field) =>
      (e) => {
        setDatalist((prev) => ({ ...prev, [field]: e.target.checked }));
      },
    []
  );

  // Memoized validation logic
  const validationErrors = useMemo(() => {
    const errors = {};

    const checkMinMax = (min, max, name) => {
      if (min != null && max != null && parseInt(min) >= parseInt(max)) {
        return `Minimum ${name} should be less than Maximum ${name}`;
      }
      return null;
    };

    // Validation checks
    errors.videoTime = checkMinMax(
      datalist?.videoMinimumSeconds,
      datalist?.videoMaximumSeconds,
      "video time"
    );
    errors.withdrawLimit = checkMinMax(
      datalist?.minimumWithdrawBeads,
      datalist?.maximumWithdrawBeads,
      "withdraw limit"
    );
    errors.buyChips = checkMinMax(
      datalist?.minimumBuyBeads,
      datalist?.maximumBuyBeads,
      "buy chips"
    );
    errors.eventPrice = checkMinMax(
      datalist?.eventPriceMinBeads,
      datalist?.eventPriceMaxBeads,
      "event price"
    );
    errors.subscriberPrice = checkMinMax(
      datalist?.subscriberChannelMinPrice,
      datalist?.subscriberChannelMaxPrice,
      "subscriber channel price"
    );
    errors.scratchCard = checkMinMax(
      datalist?.scratchCardMinChips,
      datalist?.scratchCardMaxChips,
      "scratch card chips"
    );

    return errors;
  }, [datalist]);

  const hasValidationErrors = useMemo(() => {
    return Object.values(validationErrors).some((error) => error !== null);
  }, [validationErrors]);

  // Optimized API call with error handling
  const executeApiCall = useCallback(
    async (apiCall, successMessage) => {
      try {
        dispatch(updateLoading(true));
        const result = await apiCall();
        dispatch(updateLoading(false));
        console.log("result", result);
        if (result.success) {
          return result;
        } else {
          showDangerToast(result.message || "Operation failed");
          return result;
        }
      } catch (error) {
        dispatch(updateLoading(false));
        console.error(error);
        showDangerToast(error.message || "An unexpected error occurred");
        return { success: false, error: error.message };
      }
    },
    [dispatch]
  );

  // Optimized matrix update handler
  const handleUpdateMatrixSetting = useCallback(async () => {
    setIsSubmitting(true);

    await executeApiCall(async () => {
      const postJson = { data: object };
      const res = await ApiCaller.post(
        postJson,
        urls.setMatrixGlobalWorldRate,
        userData?.access_token
      );
      console.log("res", res);
      const response = await res.response.json();
      console.log("response", response);
      if (response.status === 200 || response?.status === "200") {
        return { success: true, message: response?.message };
      } else {
        return { success: false, message: response.message };
      }
    }, "Matrix settings updated successfully!");

    setIsSubmitting(false);
  }, [object, executeApiCall, userData?.access_token]);

  // Optimized submit handler
  const setSettingData = useCallback(async () => {
    if (hasValidationErrors) {
      showDangerToast(
        "Please correct the validation errors before submitting."
      );
      return;
    }

    setIsSubmitting(true);

    await executeApiCall(async () => {
      const res = await ApiCaller.post(
        datalist,
        urls.setGlobalSetting,
        userData?.access_token
      );
      const response = await res.response.json();

      if (response.status === 200 || response?.status === "200") {
        setDatalist(response?.data);
        return { success: true, message: response?.message };
      } else {
        return { success: false, message: response.message };
      }
    }, "Settings updated successfully!");

    setIsSubmitting(false);
  }, [datalist, hasValidationErrors, executeApiCall, userData?.access_token]);

  const getAllTypeOfMatrix = useCallback(async () => {
    await executeApiCall(async () => {
      const path = urls.getAllTypeOfMatrixGlobalRate;
      const res = await ApiCaller.get({}, path, userData?.access_token);
      const response = await res.response.json();

      if (response.status === 200 || response?.status === "200") {
        setAllTypeOfAds(response?.data);
        const initialState =
          response?.data?.map((data) => ({
            typeId: data.typeId,
            rate: data?.rate,
          })) || [];
        setObject(initialState);
        return { success: true, data: response?.data };
      } else {
        return { success: false, message: response.message };
      }
    }, "Matrix data loaded successfully!");
  }, [executeApiCall, userData?.access_token]);

  // Memoized permission calculations for better performance
  const userPermissions = useSelector((state) => state?.permission);

  const permissions = useMemo(() => {
    let viewPermission = true;
    let editPermission = true;

    if (userPermissions?.modules?.length > 0) {
      const currentModulePermission = userPermissions.modules.find(
        (element) => element.moduleId === "19"
      )?.permissions;

      if (currentModulePermission) {
        viewPermission =
          currentModulePermission.find((element) => element.name === "VIEW")
            ?.isEnable ?? true;

        editPermission =
          currentModulePermission.find((element) => element.name === "EDIT")
            ?.isEnable ?? true;
      }
    }

    return { viewPermission, editPermission };
  }, [userPermissions]);

  const { viewPermission, editPermission } = permissions;

  useEffect(() => {
    dispatch(updateTopHeader("Global Setting"));
  }, [dispatch]);

  const getSettingList = useCallback(async () => {
    await executeApiCall(async () => {
      const path = urls.getGlobalSetting;
      const res = await ApiCaller.get({}, path, userData?.access_token);
      const response = await res.response.json();
      console.log("response", response);
      if (response.status === 200 || response?.status === "200") {
        setDatalist(response?.data);
        return { success: true, data: response?.data };
      } else {
        return { success: false, message: response.message };
      }
    }, "Settings loaded successfully!");
  }, [executeApiCall, userData?.access_token]);

  useEffect(() => {
    if (viewPermission) {
      getSettingList();
      if (userData?.admin) {
        getAllTypeOfMatrix();
      }
    }
  }, [viewPermission, getSettingList, getAllTypeOfMatrix, userData?.admin]);

  return (
    <>
      <NavBar />
      <div className="main-content">
        <NavBarTop />
        <div className="header">
          <div className="logo">
            <div className="header bg-gradient-primary pb-8 pt-5 pt-md-8"></div>
          </div>
        </div>
        {viewPermission ? (
          <>
            <div className="container-fluid mt--7">
              <div className="row">
                <div className="col text-left">
                  <div className="card shadow">
                    <div className="card-header border-0">
                      <div className="row align-items-center text-left">
                        <div className="col">
                          <h3 className="mb-0">Global Setting</h3>
                        </div>
                        <div className="col text-right">
                          <LoadingButton
                            onClick={setSettingData}
                            disabled={hasValidationErrors}
                            loading={isSubmitting}
                            className="btn btn-sm btn-primary text-white"
                          >
                            Submit
                          </LoadingButton>
                        </div>
                      </div>
                    </div>
                    <div className="row" style={{ margin: "10px" }}>
                      <div className="col-md-5">
                        <h4>Inactive User:</h4>
                      </div>
                      <div className="col-md-1"></div>
                      <div className="col-md-5">
                        <h4>Video Uploading Time:</h4>
                      </div>
                    </div>
                    <div className="row" style={{ marginLeft: "25px" }}>
                      <div
                        className="col-md-5"
                        style={{ border: "1px solid #f2e2e2" }}
                      >
                        <div className="row p-2">
                          <div className="col-md-6">
                            <OptimizedInput
                              label="Profile"
                              value={datalist?.noOfProfileForReport}
                              onChange={handleNumberInput(
                                "noOfProfileForReport"
                              )}
                              placeholder="Enter report no. for profile"
                              disabled={!editPermission}
                              maxLength={8}
                            />
                          </div>
                          <div className="col-md-6">
                            <OptimizedInput
                              label="Video"
                              value={datalist?.noOfVideoForReport}
                              onChange={handleNumberInput("noOfVideoForReport")}
                              placeholder="Enter report no. for Video"
                              disabled={!editPermission}
                              maxLength={8}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-md-1"></div>
                      <div
                        className="col-md-5"
                        style={{
                          border: validationErrors.videoTime
                            ? "1px solid red"
                            : "1px solid #f2e2e2",
                        }}
                      >
                        <div className="row p-2">
                          <div className="col-md-6">
                            <OptimizedInput
                              label="Minimum(Sec)"
                              value={datalist?.videoMinimumSeconds}
                              onChange={handleNumberInput(
                                "videoMinimumSeconds"
                              )}
                              placeholder="Enter Minimum Seconds"
                              disabled={!editPermission}
                              maxLength={8}
                              hasError={!!validationErrors.videoTime}
                              errorMessage={validationErrors.videoTime}
                            />
                          </div>
                          <div className="col-md-6">
                            <OptimizedInput
                              label="Maximum(Sec)"
                              value={datalist?.videoMaximumSeconds}
                              onChange={handleNumberInput(
                                "videoMaximumSeconds",
                                2
                              )}
                              placeholder="Enter Maximum Seconds"
                              disabled={!editPermission}
                              maxLength={2}
                              hasError={!!validationErrors.videoTime}
                              errorMessage={validationErrors.videoTime}
                            />
                          </div>
                          {validationErrors.videoTime && (
                            <div className="col-md-12 text-center">
                              <label className="custom-error-label">
                                {validationErrors.videoTime}
                              </label>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="row" style={{ margin: "10px" }}>
                      <div className="col-md-5">
                        <h4>Withdraw Limit:</h4>
                      </div>
                      <div className="col-md-1"></div>
                      <div className="col-md-5">
                        <h4>Sign up Chips:</h4>
                      </div>
                    </div>
                    <div className="row" style={{ marginLeft: "25px" }}>
                      <div
                        className="col-md-5"
                        style={{
                          border:
                            datalist?.maximumWithdrawBeads != null &&
                            datalist?.minimumWithdrawBeads != null &&
                            (parseInt(datalist?.maximumWithdrawBeads) <
                            parseInt(datalist?.minimumWithdrawBeads)
                              ? "1px solid red"
                              : "1px solid #f2e2e2"),
                        }}
                      >
                        <div className="row p-2">
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Maximum(Amount)
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={8}
                                disabled={!editPermission}
                                value={datalist?.maximumWithdrawBeads}
                                placeholder="Enter Maximum Withdraw Beads"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      maximumWithdrawBeads: e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Minimum(Amount)
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={8}
                                disabled={!editPermission}
                                value={datalist?.minimumWithdrawBeads}
                                placeholder="Enter Minimum Withdraw Chips"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      minimumWithdrawBeads: e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-12 text-center">
                            {datalist?.maximumWithdrawBeads != null &&
                            datalist?.minimumWithdrawBeads != null &&
                            parseInt(datalist?.maximumWithdrawBeads) <
                              parseInt(datalist?.minimumWithdrawBeads) ? (
                              <label className="custom-error-label">
                                Minimum should be less then Maximum*
                              </label>
                            ) : (
                              <></>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-1"></div>
                      <div
                        className="col-md-5"
                        style={{ border: "1px solid #f2e2e2" }}
                      >
                        <div className="row p-2">
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                No of Chips{" "}
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={8}
                                disabled={!editPermission}
                                value={datalist?.freeBeadsAfterRegistration}
                                placeholder="Enter Free Chips given after registration"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      freeBeadsAfterRegistration:
                                        e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row" style={{ margin: "10px" }}>
                      <div className="col-md-5">
                        <h4>Buy Chips Limit:</h4>
                      </div>
                      <div className="col-md-1"></div>
                      <div className="col-md-5">
                        <h4>Shop Subscription Rate:</h4>
                      </div>
                    </div>
                    <div className="row" style={{ marginLeft: "25px" }}>
                      <div
                        className="col-md-5"
                        style={{
                          border:
                            datalist?.maximumBuyBeads != null &&
                            datalist?.minimumBuyBeads != null &&
                            (parseInt(datalist?.maximumBuyBeads) <
                            parseInt(datalist?.minimumBuyBeads)
                              ? "1px solid red"
                              : "1px solid #f2e2e2"),
                        }}
                      >
                        <div className="row p-2">
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Maximum(Amount)
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={8}
                                value={datalist?.maximumBuyBeads}
                                disabled={!editPermission}
                                placeholder="Enter Maximum Withdraw Chips"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      maximumBuyBeads: e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Minimum(Amount)
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={8}
                                value={datalist?.minimumBuyBeads}
                                disabled={!editPermission}
                                placeholder="Enter Minimum Withdraw Chips"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      minimumBuyBeads: e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-12 text-center">
                            {datalist?.maximumBuyBeads != null &&
                            datalist?.minimumBuyBeads != null &&
                            parseInt(datalist?.maximumBuyBeads) <
                              parseInt(datalist?.minimumBuyBeads) ? (
                              <label className="custom-error-label">
                                Minimum should be less then Maximum*
                              </label>
                            ) : (
                              <></>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-1"></div>
                      <div
                        className="col-md-5"
                        style={{ border: "1px solid #f2e2e2" }}
                      >
                        <div className="row p-2">
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Monthly Price{" "}
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={8}
                                value={datalist?.monthlyPriceForPostInShop}
                                disabled={!editPermission}
                                placeholder="Enter monthly price for post in shop"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      monthlyPriceForPostInShop: e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Weekly Price{" "}
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={8}
                                disabled={!editPermission}
                                value={datalist?.weeklyPriceForPostInShop}
                                placeholder="Enter Weekly Price for Post in Shop"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      weeklyPriceForPostInShop: e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row" style={{ margin: "10px" }}>
                      <div className="col-md-5">
                        <h4>Admin Commission on Withdraw:</h4>
                      </div>
                      <div className="col-md-1"></div>
                      <div className="col-md-5">
                        <h4>Exclusive Pricing:</h4>
                      </div>
                    </div>
                    <div className="row" style={{ marginLeft: "25px" }}>
                      <div
                        className="col-md-5"
                        style={{ border: "1px solid #f2e2e2" }}
                      >
                        <div className="row p-2">
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Enter % of commission
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={3}
                                value={datalist?.percentageOfCommission}
                                disabled={!editPermission}
                                placeholder="Enter % of commission"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      percentageOfCommission: e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-1"></div>
                      <div
                        className="col-md-5"
                        style={{
                          border:
                            datalist?.maximumBuyBeads != null &&
                            datalist?.minimumBuyBeads != null &&
                            (parseInt(datalist?.maximumBuyBeads) <
                            parseInt(datalist?.minimumBuyBeads)
                              ? "1px solid red"
                              : "1px solid #f2e2e2"),
                        }}
                      >
                        <div className="row p-2">
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Maximum(Amount)
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={8}
                                value={datalist?.maximumExclusivePrice}
                                disabled={!editPermission}
                                placeholder="Enter Maximum Exclusive Chips"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      maximumExclusivePrice: e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Minimum(Amount)
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={8}
                                disabled={!editPermission}
                                value={datalist?.minimumExclusivePrice}
                                placeholder="Enter Minimum Exclusive Chips"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      minimumExclusivePrice: e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-12 text-center">
                            {datalist?.maximumBuyBeads != null &&
                            datalist?.minimumBuyBeads != null &&
                            parseInt(datalist?.maximumBuyBeads) <
                              parseInt(datalist?.minimumBuyBeads) ? (
                              <label className="custom-error-label">
                                Minimum should be less then Maximum*
                              </label>
                            ) : (
                              <></>
                            )}
                          </div>
                        </div>
                      </div>
                      {/* <div
                    className='col-md-5'
                    style={{
                      border: '1px solid #f2e2e2',
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'center'
                    }}
                  >
                    <div className='beads-info'>
                      Number of Chips
                      <span className='beads-amount'>
                        <input
                          maxLength={7}
                          value={datalist?.numberOfBeadsEqual}
                          onChange={(e) =>
                            handleInputChange(e, 'numberOfBeadsEqual')
                          }
                          type='text'
                          className='custom-input'
                          placeholder=''
                        />
                      </span>
                      is equal to
                      <span className='ngn-amount'>
                        <input
                          maxLength={7}
                          value={datalist?.amountOfNGNEqual}
                          onChange={(e) =>
                            handleInputChange(e, 'amountOfNGNEqual')
                          }
                          type='text'
                          className='custom-input'
                          placeholder=''
                        />
                        INR
                      </span>
                      .
                    </div>
                  </div> */}
                    </div>
                    <div className="row" style={{ margin: "10px" }}>
                      <div className="col-md-5">
                        <h4>Event Price:</h4>
                      </div>
                      <div className="col-md-1"></div>
                      <div className="col-md-5">
                        <h4>Ad View Time:</h4>
                      </div>
                    </div>
                    <div className="row" style={{ marginLeft: "25px" }}>
                      <div
                        className="col-md-5"
                        style={{
                          border:
                            datalist?.eventPriceMaxBeads != null &&
                            datalist?.eventPriceMinBeads != null &&
                            (parseInt(datalist?.eventPriceMaxBeads) <
                            parseInt(datalist?.eventPriceMinBeads)
                              ? "1px solid red"
                              : "1px solid #f2e2e2"),
                        }}
                      >
                        <div className="row p-2">
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Min Chips
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={8}
                                value={datalist?.eventPriceMinBeads}
                                disabled={!editPermission}
                                placeholder="Enter Min Chips"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      eventPriceMinBeads: parseInt(
                                        e.target.value
                                      ),
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Max Chips
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={8}
                                value={datalist?.eventPriceMaxBeads}
                                placeholder="Enter Max Chips"
                                disabled={!editPermission}
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      eventPriceMaxBeads: parseInt(
                                        e.target.value
                                      ),
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-12 text-center">
                            {datalist?.eventPriceMaxBeads != null &&
                              datalist?.eventPriceMinBeads != null &&
                              parseInt(datalist?.eventPriceMaxBeads) <
                                parseInt(datalist?.eventPriceMinBeads) && (
                                <label className="custom-error-label">
                                  Minimum should be less then Maximum*
                                </label>
                              )}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-1"></div>
                      <div
                        className="col-md-5"
                        style={{ border: "1px solid #f2e2e2" }}
                      >
                        <div className="row p-2">
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Sec/View{" "}
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={5}
                                value={datalist?.perViewSec}
                                disabled={!editPermission}
                                placeholder="Enter Sec/View"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      perViewSec: e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row" style={{ margin: "10px" }}>
                      <div className="col-md-5">
                        <h4>Subscriber Channel Price/Month:</h4>
                      </div>
                      <div className="col-md-1"></div>
                      <div className="col-md-5">
                        <h4>Badge Price:</h4>
                      </div>
                    </div>
                    <div className="row" style={{ marginLeft: "25px" }}>
                      <div
                        className="col-md-5"
                        style={{
                          border:
                            datalist?.subscriberChannelMaxPrice != null &&
                            datalist?.subscriberChannelMinPrice != null &&
                            (parseInt(datalist?.subscriberChannelMaxPrice) <
                            parseInt(datalist?.subscriberChannelMinPrice)
                              ? "1px solid red"
                              : "1px solid #f2e2e2"),
                        }}
                      >
                        <div className="row p-2">
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Min Chips
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={8}
                                disabled={!editPermission}
                                value={datalist?.subscriberChannelMinPrice}
                                placeholder="Enter Min Chips"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      subscriberChannelMinPrice: parseInt(
                                        e.target.value
                                      ),
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Max Chips
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={8}
                                value={datalist?.subscriberChannelMaxPrice}
                                disabled={!editPermission}
                                placeholder="Enter Max Chips"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      subscriberChannelMaxPrice: parseInt(
                                        e.target.value
                                      ),
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-12 text-center">
                            {datalist?.subscriberChannelMaxPrice != null &&
                              datalist?.subscriberChannelMinPrice != null &&
                              parseInt(datalist?.subscriberChannelMaxPrice) <
                                parseInt(
                                  datalist?.subscriberChannelMinPrice
                                ) && (
                                <label className="custom-error-label">
                                  Minimum should be less then Maximum*
                                </label>
                              )}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-1"></div>
                      <div
                        className="col-md-5"
                        style={{ border: "1px solid #f2e2e2" }}
                      >
                        <div className="row p-2">
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Chips{" "}
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={8}
                                disabled={!editPermission}
                                value={datalist?.badgeBeadsPrice}
                                placeholder="Enter Chips"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      badgeBeadsPrice: e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row" style={{ margin: "10px" }}>
                      <div className="col-md-5">
                        <h4>Influencer Eligibility:</h4>
                      </div>
                      <div className="col-md-1"></div>
                      <div className="col-md-5">
                        <h4>Scratch Card Limit:</h4>
                      </div>
                    </div>
                    <div className="col-md-1"></div>
                    <div className="row" style={{ marginLeft: "25px" }}>
                      <div
                        className="col-md-5"
                        style={{ border: "1px solid #f2e2e2" }}
                      >
                        <div className="row p-2">
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Follower's Influencer
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={8}
                                disabled={!editPermission}
                                value={datalist?.followersForInfluencer}
                                placeholder="Enter followers for Influencer"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      followersForInfluencer: e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Influencer Views
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={8}
                                value={datalist?.viewsForInfluencer}
                                placeholder="Enter views For Influencer"
                                className="form-control"
                                disabled={!editPermission}
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      viewsForInfluencer: e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Years Spend on platform(Month)
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={8}
                                value={datalist?.yearsSpendForInfluence}
                                placeholder="Enter Month"
                                className="form-control"
                                disabled={!editPermission}
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      yearsSpendForInfluence: e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Contact Fee(In Chips)
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={8}
                                value={datalist?.influencerContactCharge}
                                disabled={!editPermission}
                                placeholder="Enter Contact Fee"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      influencerContactCharge: e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-1"></div>
                      <div
                        className="col-md-5"
                        style={{ border: "1px solid #f2e2e2" }}
                      >
                        <div className="row p-2">
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Maximum(Day)
                              </Form.Label>
                              <Form.Control
                                type="text"
                                disabled={!editPermission}
                                maxLength={8}
                                value={datalist?.maxScratchCardPerDay}
                                placeholder="Enter Max Scratch Card Per Day"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      maxScratchCardPerDay: e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Maximum(Week)
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={8}
                                value={datalist?.maxScratchCardPerWeek}
                                disabled={!editPermission}
                                placeholder="Enter Max Scratch Card Per Week"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      maxScratchCardPerWeek: e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Maximum(Year)
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={8}
                                disabled={!editPermission}
                                value={datalist?.maxScratchCardPerMonth}
                                placeholder="Enter Max Scratch Card Per Month"
                                className="form-control-inWidth"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      maxScratchCardPerMonth: e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row" style={{ margin: "10px" }}>
                      <div className="col-md-5">
                        <h4>Influencer Earning Eligibility:</h4>
                      </div>
                      <div className="col-md-1"></div>
                      <div className="col-md-5">
                        <h4>Scratch Card Time Frame:</h4>
                      </div>
                    </div>
                    <div className="col-md-1"></div>
                    <div className="row" style={{ marginLeft: "25px" }}>
                      <div
                        className="col-md-5"
                        style={{ border: "1px solid #f2e2e2" }}
                      >
                        <div className="row p-2">
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Minimum Followers
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={8}
                                disabled={!editPermission}
                                value={
                                  datalist?.minFollowersForInfluencerEarning
                                }
                                placeholder="Enter Minimum Followers For Influencer Earning"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      minFollowersForInfluencerEarning:
                                        e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Minimum Influencer Earning By Followers
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={8}
                                disabled={!editPermission}
                                value={
                                  datalist?.minInfluencerEarningByFollowers
                                }
                                placeholder="Enter Influencer Earning By Followers"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      minInfluencerEarningByFollowers:
                                        e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Minimum Views
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={8}
                                disabled={!editPermission}
                                value={datalist?.minViewsForInfluencerEarning}
                                placeholder="Min Views"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      minViewsForInfluencerEarning:
                                        e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Minimum Influencer Earning By Views
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={8}
                                disabled={!editPermission}
                                value={datalist?.minInfluencerEarningByViews}
                                placeholder="Enter Minimum Influencer Earning By Views"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      minInfluencerEarningByViews:
                                        e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-1"></div>
                      <div
                        className="col-md-5"
                        style={{ border: "1px solid #f2e2e2" }}
                      >
                        <div className="row p-2">
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Expiry Time
                              </Form.Label>
                              <Form.Control
                                type="text"
                                disabled={!editPermission}
                                maxLength={8}
                                value={datalist?.scratchCardExpiryTime}
                                placeholder="Enter Scratch Card Expiry Time"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      scratchCardExpiryTime: e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Earn Scratch Card
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={8}
                                disabled={!editPermission}
                                value={
                                  datalist?.noOfVideosToWatchEarnScratchCard
                                }
                                placeholder="Enter no. Of Videos To Watch Earn Scratch Card"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      noOfVideosToWatchEarnScratchCard:
                                        e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Time Frame (Minutes)
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={8}
                                disabled={!editPermission}
                                value={
                                  datalist?.timeFrameLimitPerScratchCardInMinutes
                                }
                                placeholder="Enter time Frame Limit Per Scratch Card In Minutes"
                                className="form-control-inWidth"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      timeFrameLimitPerScratchCardInMinutes:
                                        e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row" style={{ margin: "10px" }}>
                      <div className="col-md-5">
                        <h4>Scratch Card Chips:</h4>
                      </div>
                      <div className="col-md-1"></div>
                      <div className="col-md-5">
                        <h4>Others:</h4>
                      </div>
                    </div>
                    <div className="row" style={{ marginLeft: "25px" }}>
                      <div className="col-md-5">
                        <div className="row">
                          <div
                            className="col-md-12"
                            style={{
                              border:
                                datalist?.scratchCardMaxChips != null &&
                                datalist?.scratchCardMinChips != null &&
                                (parseInt(datalist?.scratchCardMaxChips) <
                                parseInt(datalist?.scratchCardMinChips)
                                  ? "1px solid red"
                                  : "1px solid #f2e2e2"),
                            }}
                          >
                            <div className="row p-2">
                              <div className="col-md-6">
                                <Form.Group
                                  className="mb-3"
                                  controlId="exampleForm.ControlInput1"
                                >
                                  <Form.Label className="text-small">
                                    Minimum(Card Chips)
                                  </Form.Label>
                                  <Form.Control
                                    type="text"
                                    disabled={!editPermission}
                                    maxLength={8}
                                    value={datalist?.scratchCardMinChips}
                                    placeholder="Enter Minimum scratch Card Chips"
                                    className="form-control"
                                    onChange={(e) => {
                                      // Use a regular expression to check if the input is numeric
                                      const numericRegex = /^[0-9\b]+$/;
                                      // Check if the input value matches the numeric regex
                                      if (
                                        numericRegex.test(e.target.value) ||
                                        e.target.value === ""
                                      ) {
                                        // Update the state only if the input is numeric or empty
                                        setDatalist((prevData) => ({
                                          ...prevData,
                                          scratchCardMinChips: e.target.value,
                                        }));
                                      }
                                    }}
                                  />
                                </Form.Group>
                              </div>
                              <div className="col-md-6">
                                <Form.Group
                                  className="mb-3"
                                  controlId="exampleForm.ControlInput1"
                                >
                                  <Form.Label className="text-small">
                                    Maximum(Card chips)
                                  </Form.Label>
                                  <Form.Control
                                    type="text"
                                    maxLength={8}
                                    disabled={!editPermission}
                                    value={datalist?.scratchCardMaxChips}
                                    placeholder="Enter Maximum scratch Card Chips"
                                    className="form-control"
                                    onChange={(e) => {
                                      // Use a regular expression to check if the input is numeric
                                      const numericRegex = /^[0-9\b]+$/;
                                      // Check if the input value matches the numeric regex
                                      if (
                                        numericRegex.test(e.target.value) ||
                                        e.target.value === ""
                                      ) {
                                        // Update the state only if the input is numeric or empty
                                        setDatalist((prevData) => ({
                                          ...prevData,
                                          scratchCardMaxChips: e.target.value,
                                        }));
                                      }
                                    }}
                                  />
                                </Form.Group>
                              </div>
                              <div className="col-md-12 text-center">
                                {datalist?.scratchCardMaxChips != null &&
                                datalist?.scratchCardMinChips != null &&
                                parseInt(datalist?.scratchCardMaxChips) <
                                  parseInt(datalist?.scratchCardMinChips) ? (
                                  <label className="custom-error-label">
                                    Minimum should be less then Maximum*
                                  </label>
                                ) : (
                                  <></>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="row mt-3">
                          <h4>Invite Friend:</h4>
                          <div
                            className="col-md-12"
                            style={{ border: "1px solid #f2e2e2" }}
                          >
                            <div className="row p-2">
                              {/* Referral Configuration Settings */}
                              <div className="col-md-12">
                                <div className="row">
                                  <div className="col-md-6 col-lg-6">
                                    <div className="p-2" style={{ border: '1px solid #e9ecef', borderRadius: '4px', minHeight: '60px' }}>
                                      <OptimizedToggle
                                        label="Enable Referral"
                                        checked={datalist?.referralEnable}
                                        onChange={handleBooleanToggle("referralEnable")}
                                        disabled={!editPermission}
                                        id="referralEnable"
                                      />
                                    </div>
                                  </div>
                                  <div className="col-md-6 col-lg-6">
                                    <div className="p-2" style={{ border: '1px solid #e9ecef', borderRadius: '4px', minHeight: '60px' }}>
                                      <OptimizedToggle
                                        label="Paid Network Only"
                                        checked={datalist?.referralEnableForOnlyPaidNetwork}
                                        onChange={handleBooleanToggle("referralEnableForOnlyPaidNetwork")}
                                        disabled={!editPermission}
                                        id="referralEnableForOnlyPaidNetwork"
                                      />
                                    </div>
                                  </div>
                                  <div className="col-md-6 col-lg-6">
                                    <div className="p-2" style={{ border: '1px solid #e9ecef', borderRadius: '4px', minHeight: '60px' }}>
                                      <OptimizedToggle
                                        label="From Referral"
                                        checked={datalist?.fromReferral}
                                        onChange={handleBooleanToggle("fromReferral")}
                                        disabled={!editPermission}
                                        id="fromReferral"
                                      />
                                    </div>
                                  </div>
                                  <div className="col-md-6 col-lg-6">
                                    <div className="p-2" style={{ border: '1px solid #e9ecef', borderRadius: '4px', minHeight: '60px' }}>
                                      <OptimizedToggle
                                        label="To Referral"
                                        checked={datalist?.toReferral}
                                        onChange={handleBooleanToggle("toReferral")}
                                        disabled={!editPermission}
                                        id="toReferral"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Referral Rewards - Data Bundle */}
                              <div className="col-md-12 mt-3">
                                <div className="row">
                                  <div className="col-md-6">
                                    <OptimizedDecimalInput
                                      label="From Referral Data Bundle"
                                      value={datalist?.fromReferralDataBundle}
                                      onChange={handleDecimalInput("fromReferralDataBundle")}
                                      placeholder="Enter data bundle amount"
                                      disabled={!editPermission}
                                      maxLength={8}
                                    />
                                  </div>
                                  <div className="col-md-6">
                                    <OptimizedDecimalInput
                                      label="To Referral Data Bundle"
                                      value={datalist?.toReferralDataBundle}
                                      onChange={handleDecimalInput("toReferralDataBundle")}
                                      placeholder="Enter data bundle amount"
                                      disabled={!editPermission}
                                      maxLength={8}
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Referral Rewards - Chips */}
                              <div className="col-md-12 mt-3">
                                <div className="row">
                                  <div className="col-md-6">
                                    <OptimizedDecimalInput
                                      label="From Referral Chips"
                                      value={datalist?.fromReferralChips}
                                      onChange={handleDecimalInput("fromReferralChips")}
                                      placeholder="Enter chips amount"
                                      disabled={!editPermission}
                                      maxLength={8}
                                    />
                                  </div>
                                  <div className="col-md-6">
                                    <OptimizedDecimalInput
                                      label="To Referral Chips"
                                      value={datalist?.toReferralChips}
                                      onChange={handleDecimalInput("toReferralChips")}
                                      placeholder="Enter chips amount"
                                      disabled={!editPermission}
                                      maxLength={8}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-1"></div>
                      <div
                        className="col-md-5"
                        style={{ border: "1px solid #f2e2e2" }}
                      >
                        <div className="row p-2">
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Between Reels{" "}
                              </Form.Label>
                              <Form.Control
                                type="text"
                                disabled={!editPermission}
                                maxLength={8}
                                value={datalist?.noOfVideosBetweenTwoAds}
                                placeholder="Enter Position"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      noOfVideosBetweenTwoAds: e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Panel on Reels
                              </Form.Label>
                              <Form.Control
                                type="text"
                                disabled={!editPermission}
                                maxLength={8}
                                value={datalist?.noOfVideosPanelOnReelAds}
                                placeholder="Enter Position"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      noOfVideosPanelOnReelAds: e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Delete Watch History(Days){" "}
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={8}
                                disabled={!editPermission}
                                value={
                                  datalist?.noOfDaysToDeleteUserWatchHistory
                                }
                                placeholder="Enter no. Of Days To Delete User Watch History"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      noOfDaysToDeleteUserWatchHistory:
                                        e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Story Play(Sec){" "}
                              </Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={8}
                                disabled={!editPermission}
                                value={datalist?.storyPlayTimeInSec}
                                placeholder="Enter story Play Time In Sec"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      storyPlayTimeInSec: e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                FeedBack Email{" "}
                              </Form.Label>
                              <Form.Control
                                type="Email"
                                maxLength={8}
                                disabled={!editPermission}
                                value={datalist?.feedBackEmail}
                                placeholder="Enter FeedBack Email"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      feedBackEmail: e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Coin Transfer Rate{" "}
                              </Form.Label>
                              <Form.Control
                                type="Email"
                                maxLength={10}
                                disabled={!editPermission}
                                value={datalist?.cointransferRate}
                                placeholder="Enter Transfer Rate"
                                className="form-control"
                                onChange={(e) => {
                                  // Use a regular expression to check if the input is numeric
                                  const numericRegex = /^[0-9\b]+$/;
                                  // Check if the input value matches the numeric regex
                                  if (
                                    numericRegex.test(e.target.value) ||
                                    e.target.value === ""
                                  ) {
                                    // Update the state only if the input is numeric or empty
                                    setDatalist((prevData) => ({
                                      ...prevData,
                                      cointransferRate: e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Cashback Redirect URL:{" "}
                              </Form.Label>
                              <Form.Control
                                type="text"
                                disabled={!editPermission}
                                value={datalist?.cashbackRedirectUrl}
                                placeholder="Enter Redirect URL"
                                className="form-control-inWidth"
                                onChange={(e) => {
                                  setDatalist((prevData) => ({
                                    ...prevData,
                                    cashbackRedirectUrl: e.target.value,
                                  }));
                                }}
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6"></div>
                          <div className="col-md-6">
                            <Form.Group
                              className="mb-3"
                              controlId="exampleForm.ControlInput1"
                            >
                              <Form.Label className="text-small">
                                Supa Pay API Token:{" "}
                              </Form.Label>
                              <Form.Control
                                type="text"
                                disabled={!editPermission}
                                value={datalist?.supaPayToken}
                                placeholder="Enter Token"
                                className="form-control-inWidth"
                                onChange={(e) => {
                                  setDatalist((prevData) => ({
                                    ...prevData,
                                    supaPayToken: e.target.value,
                                  }));
                                }}
                              />
                            </Form.Group>
                          </div>
                        </div>
                      </div>
                    </div>{" "}
                    <hr />
                    {userData.admin && (
                      <>
                        <div className="row" style={{ margin: "10px" }}>
                          <div className="col-md-8">
                            <h4>Ads Matrix World Rate</h4>
                          </div>
                          <div className="col-md-4">
                            <LoadingButton
                              onClick={handleUpdateMatrixSetting}
                              loading={isSubmitting}
                              className="btn btn-sm btn-primary text-white"
                            >
                              Update Submit
                            </LoadingButton>
                          </div>
                        </div>
                        <div className="row" style={{ marginLeft: "25px" }}>
                          <div className="col-md-6">
                            <table>
                              <thead>
                                <tr>
                                  {allTypeOfAds?.map((data) => (
                                    <th
                                      className="custom-matrix-th"
                                      key={data.typeId}
                                    >
                                      {data?.name}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  {object.map((data, index) => (
                                    <td
                                      className="custom-matrix-td"
                                      key={index}
                                    >
                                      <input
                                        style={{ maxWidth: "100px" }}
                                        value={data.rate}
                                        maxLength={8}
                                        onChange={(e) =>
                                          handleRateInputChange(
                                            index,
                                            "rate",
                                            e.target.value
                                          )
                                        }
                                      />
                                    </td>
                                  ))}
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    )}
                    <div className="row mb-5"></div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <InsufficientPermission />
        )}
      </div>
    </>
  );
};
